import { Box, FormControl, FormLabel, FormErrorMessage, Text, VStack, HStack, Spinner, useColorModeValue } from "@chakra-ui/react";
import { useState, useRef, useLayoutEffect, forwardRef, useImperativeHandle, useEffect } from "react";

import { $ok } from "foundation-ts/commons";
import { TSError } from "foundation-ts/tserrors";
import { Nullable } from "foundation-ts/types";

import { FieldKey, MollieComponent, MollieComponentEvent, MollieInstance, PaymentFormRef } from "./types";

interface CreditCardCardProps {
    signup?: boolean;
    labelColor: string;
    mollie: Nullable<MollieInstance>;
    mollieReady: boolean;
    onSubmit: (token: string, captchaToken?: string) => Promise<void>;
    onValidityChange: (isValid: boolean) => void;
}

export const CreditCardCard = forwardRef<PaymentFormRef, CreditCardCardProps>((props, ref) => {
    useImperativeHandle(ref, () => ({
        submit: async (captchaToken?: string) => {
            const { token, error } = await mollie!.createToken();
            if ($ok(error) || !$ok(token)) { TSError.throw(error?.message ?? "Erreur d'initialisation du service mollie", -1); }

            await onSubmit(token!, captchaToken);
        }
    }));

    const { mollie, mollieReady, labelColor, onSubmit, onValidityChange } = props;
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
        cardHolder: null,
        cardNumber: null,
        expiryDate: null,
        verificationCode: null,
    });
    const mountedRef = useRef<boolean>(false);
    const componentsRef = useRef<Partial<Record<FieldKey, MollieComponent>>>({});
    const eventListenersRef = useRef<Array<() => void>>([]);
    const fieldValidsRef = useRef<Record<FieldKey, boolean>>({
        cardHolder: false,
        cardNumber: false,
        expiryDate: false,
        verificationCode: false,
    });

    const mandateBg = "orange.900";
    const mandateText = "orange.100";
    const mandateBorder = "orange.600";
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const mollieStyles = {
        base: {
            color: "#E2E8F0",
            fontSize: "14px",
            lineHeight: "38px",
            "::placeholder": { color: "#4A5568" },
        },
        valid:   { color:"#E2E8F0" },
        invalid: { color: "#E53E3E" },
    };

    function initMollie(): void {
        if (mountedRef.current) { return; }
        mountedRef.current = true;

        const cardHolder       = mollie?.createComponent("cardHolder",       { styles: mollieStyles });
        const cardNumber       = mollie?.createComponent("cardNumber",       { styles: mollieStyles });
        const expiryDate       = mollie?.createComponent("expiryDate",       { styles: mollieStyles });
        const verificationCode = mollie?.createComponent("verificationCode", { styles: mollieStyles });
 
        const mountMap: Array<{ component?: MollieComponent; selector: string; key: FieldKey }> = [
            { component: cardHolder,      selector: "#mollie-card-holder",        key: "cardHolder" },
            { component: cardNumber,      selector: "#mollie-card-number",        key: "cardNumber" },
            { component: expiryDate,      selector: "#mollie-expiry-date",        key: "expiryDate" },
            { component: verificationCode,selector: "#mollie-verification-code",  key: "verificationCode" },
        ];
 
        const mounted: Partial<Record<FieldKey, MollieComponent>> = {};
        mountMap.forEach(({ component, selector, key }) => {
            if (!$ok(document.querySelector(selector))) { return; }

            component?.mount(selector);
            mounted[key] = component;
            const listener = (event: MollieComponentEvent) => {
                fieldValidsRef.current[key] = !$ok(event.error) && event.valid;
                setFieldErrors((prev) => ({
                    ...prev,
                    [key]: event.error,
                }));
                onValidityChange(Object.values(fieldValidsRef.current).every(Boolean));
            };
            component?.addEventListener("change", listener);
            eventListenersRef.current.push(() => {
                component?.removeEventListener("change", listener);
            });
        })

        componentsRef.current = mounted;
    }

    const mollieField = (id: string, errorKey: FieldKey, showSpinner: boolean = false) => (
        <>
            {showSpinner && !mollieReady && (
                <HStack h="40px" px={3} border="1px solid" borderColor={borderColor} borderRadius="md" bg="transparent">
                    <Spinner size="xs" color="gray.400" />
                    <Text fontSize="xs" color="gray.400">Chargement sécurisé…</Text>
                </HStack>
            )}
            <Box id={id} h="40px" px={3} border="1px solid" borderColor={fieldErrors[errorKey] ? "red.400" : borderColor} borderRadius="md" bg="transparent"
                 sx={mollieReady ? {
                    "&::selection, &:focus, &:active": {
                        backgroundColor: "transparent !important",
                        color: "inherit !important",
                    },
                } : { display: "none" }}
             />
        </>
    );

    useLayoutEffect(() => {
        return () => {
            mountedRef.current = false;
            Object.values(componentsRef.current).forEach((c) => {
                try {
                    c?.unmount();
                } catch {
                }
            });
            eventListenersRef.current.forEach((cleanup) => cleanup());
            eventListenersRef.current = [];
            componentsRef.current = {};
        };
    }, []);

    useEffect(() => {
        if ($ok(mollie)) {
            initMollie();
        }
    }, []);

    return (
        <VStack spacing={4}>
            <FormControl isRequired isInvalid={$ok(fieldErrors.cardHolder)}>
                <FormLabel fontSize="sm" color={labelColor}>Titulaire de la carte</FormLabel>
                { mollieField("mollie-card-holder", "cardHolder", true) }
                <Text fontSize="xs" color="gray.500" mt={1}>
                    Saisissez le nom tel qu'il apparaît sur la carte
                </Text>
                <FormErrorMessage color="red.200">{fieldErrors.cardHolder}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={$ok(fieldErrors.cardNumber)}>
                <FormLabel fontSize="sm" color={labelColor}>Numéro de carte</FormLabel>
                { mollieField("mollie-card-number", "cardNumber") }
                <FormErrorMessage color="red.200">{fieldErrors.cardNumber}</FormErrorMessage>
            </FormControl>

            <HStack w="full" spacing={4} align="flex-start">
                <FormControl isRequired isInvalid={$ok(fieldErrors.expiryDate)}>
                    <FormLabel fontSize="sm" color={labelColor}>Expiration</FormLabel>
                    { mollieField("mollie-expiry-date", "expiryDate") }
                    <FormErrorMessage color="red.200">{fieldErrors.expiryDate}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={$ok(fieldErrors.verificationCode)}>
                    <FormLabel fontSize="sm" color={labelColor}>CVV</FormLabel>
                    { mollieField("mollie-verification-code", "verificationCode") }
                    <FormErrorMessage color="red.200">{fieldErrors.verificationCode}</FormErrorMessage>
                </FormControl>
            </HStack>

            <Box mt={5} p={4} bg={mandateBg} border="1px solid" borderColor={mandateBorder} borderRadius="md">
                <Text fontSize="xs" color={mandateText} lineHeight="1.7">
                    Un premier paiement d'un montant de 0€ est initié. En validant ce paiement, un mandat Carte Bancaire est créé 
                    et sera automatiquement utilisé pour les prochains paiements.
                </Text>
            </Box>
        </VStack>
    );
});

type FieldErrors = Record<FieldKey, string | null>;